import { CalendarIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import { capitalize, isEmpty, upperCase } from "lodash";
import React from "react";
import { Coupon, CouponTermType, PurchaseItem } from "types";
import { useProducts } from "../../hooks/resources/useProduct";
import { useResource } from "../../hooks/useResource";
import { getCouponLineItems } from "../../models/PurchaseItem";
import { NoTranslate } from "../Common/NoTranslate";
import { PromptModal } from "../Common/PromptModal";

interface CouponValidationModalProps {
    isOpen: boolean;
    item: PurchaseItem;
    arrivalRestrictionMessage?: string;
    onClose: () => void;
    onSubmit: () => void;
}

export const CouponValidationModal = ({
    isOpen,
    item,
    arrivalRestrictionMessage,
    onClose,
    onSubmit,
}: CouponValidationModalProps) => {
    const couponLineItems = getCouponLineItems(item);
    const couponFromItem = couponLineItems?.[0]?.coupon;
    const couponCode = couponLineItems?.[0]?.couponCode?.code;

    const { products } = useProducts();
    const { data: coupon } = useResource<Coupon>(["coupons", couponFromItem?.id]);

    const purchaseWindowTerm = coupon?.terms?.filter((term) => term.type === CouponTermType.PURCHASE_WINDOW);
    const arrivalWindowTerm = coupon?.terms?.filter((term) => term.type === CouponTermType.ARRIVAL_WINDOW);
    const productsTerm = coupon?.terms?.find((term) => term.type === CouponTermType.PRODUCTS);

    const noOfProducts = productsTerm?.products?.length ?? 0;

    const suffixCaption = noOfProducts > 3 ? ` and ${noOfProducts - 3} other experiences` : "";

    const productNames =
        productsTerm?.products
            ?.slice(0, 3)
            .map((productId) => products.find((product) => product.id === productId)?.name) ?? [];

    const productsTermCaption = productNames.join(",") + suffixCaption;

    const arrivalTermCaption = arrivalWindowTerm?.length ? capitalize(arrivalWindowTerm[0]?.caption) : "";
    const arrivalRestriction = isEmpty(arrivalRestrictionMessage) ? arrivalTermCaption : arrivalRestrictionMessage;

    return (
        <PromptModal readOnly title="Restricted Coupon" isOpen={isOpen} onConfirm={onSubmit} onClose={onClose}>
            <div className="space-y-4 p-5">
                <p>
                    This coupon code{" "}
                    <b>
                        <var data-var="couponCode">({upperCase(couponCode)})</var>
                    </b>{" "}
                    cannot be applied to this booking due to restrictions on the coupon.
                </p>
                {purchaseWindowTerm && purchaseWindowTerm.length > 0 && (
                    <div>
                        <h3>Purchase Restriction</h3>
                        <div className="space-x-1">
                            <CalendarIcon />
                            <span>
                                {capitalize(purchaseWindowTerm[0]?.caption?.replace("currentTime", "Purchase Date")) ??
                                    "None"}
                            </span>
                        </div>
                    </div>
                )}
                {arrivalRestriction && (
                    <div>
                        <h3>Arrival Restriction</h3>
                        <div className="space-x-1">
                            <CalendarIcon />
                            <span className="whitespace-pre-line">{arrivalRestriction}</span>
                        </div>
                    </div>
                )}
                <div>
                    <h3>Product Restriction</h3>
                    <p>
                        Applies to:{" "}
                        <Tooltip
                            content="Check Coupon Restrictions settings to see all experiences"
                            disabled={noOfProducts <= 3}
                        >
                            {productsTermCaption ? <NoTranslate>{productsTermCaption}</NoTranslate> : "All listings"}
                        </Tooltip>
                    </p>
                </div>
            </div>
        </PromptModal>
    );
};
